import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Save, Trash2, Edit2, List, Type, Hash, CheckSquare, Image as ImageIcon, Calendar } from "lucide-react";
import { toast } from "react-toastify";

const initialQuestionData = {
  question_text: "",
  question_type: "text",
  is_required: true,
  options: "",
  option_keys: "",
  min_value: "",
  max_value: "",
  min_length: "",
  max_length: "",
  allowed_extensions: "",
  max_file_size_mb: "",
  sort_order: 0,
  is_active: true,
  helper_text: "",
};

const questionTypes = [
  { value: "text", label: "Text (Short Answer)", icon: Type },
  { value: "textarea", label: "Textarea (Paragraph)", icon: List },
  { value: "numeric", label: "Numeric", icon: Hash },
  { value: "radio", label: "Radio Buttons", icon: CheckSquare },
  { value: "checkbox", label: "Checkboxes", icon: CheckSquare },
  { value: "dropdown", label: "Dropdown", icon: List },
  { value: "file", label: "File Upload", icon: ImageIcon },
  { value: "date", label: "Date", icon: Calendar },
  { value: "boolean", label: "Yes/No (Boolean)", icon: CheckSquare },
];

const AwardQuestionsModal = ({ isOpen, onClose, apiCall, award }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editQuestionData, setEditQuestionData] = useState(null);
  const [formData, setFormData] = useState(initialQuestionData);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && award) {
      fetchQuestions();
      closeForm();
    }
  }, [isOpen, award]);

  const openCreateForm = () => {
    setEditQuestionData(null);
    setFormData(initialQuestionData);
    setIsCreating(true);
  };

  const openEditForm = (q) => {
    setEditQuestionData(q);
    setFormData({
      question_text: q.question_text || "",
      question_type: q.question_type || "text",
      is_required: q.is_required ?? true,
      options: q.options || "",
      option_keys: q.option_keys || "",
      min_value: q.min_value !== null ? q.min_value : "",
      max_value: q.max_value !== null ? q.max_value : "",
      min_length: q.min_length !== null ? q.min_length : "",
      max_length: q.max_length !== null ? q.max_length : "",
      allowed_extensions: q.allowed_extensions || "",
      max_file_size_mb: q.max_file_size_mb !== null ? q.max_file_size_mb : "",
      sort_order: q.sort_order !== null ? q.sort_order : 0,
      is_active: q.is_active ?? true,
      helper_text: q.helper_text || "",
    });
    setIsCreating(true);
  };

  const closeForm = () => {
    setIsCreating(false);
    setEditQuestionData(null);
    setFormData(initialQuestionData);
  };

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/awards/questions/${award.id}`);
      setQuestions(data || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.question_text.trim()) {
      toast.error("Question text is required");
      return;
    }

    if (['radio', 'checkbox', 'dropdown'].includes(formData.question_type)) {
      if (!formData.options || !formData.options.trim()) {
        toast.error("Options are required for this question type.");
        return;
      }
      if (formData.option_keys && formData.option_keys.trim()) {
        const optsCount = formData.options.split(',').filter(s => s.trim().length > 0).length;
        const keysCount = formData.option_keys.split(',').filter(s => s.trim().length > 0).length;
        if (optsCount !== keysCount) {
          toast.error(`Mismatch: You provided ${optsCount} option(s) but ${keysCount} option key(s). They must be equal.`);
          return;
        }
      }
    }

    if (['text', 'textarea'].includes(formData.question_type)) {
      const minL = formData.min_length !== "" ? parseInt(formData.min_length, 10) : null;
      const maxL = formData.max_length !== "" ? parseInt(formData.max_length, 10) : null;
      if (minL !== null && maxL !== null && minL > maxL) {
        toast.error("Min Length cannot be greater than Max Length.");
        return;
      }
    }

    if (formData.question_type === 'numeric') {
      const minV = formData.min_value !== "" ? parseFloat(formData.min_value) : null;
      const maxV = formData.max_value !== "" ? parseFloat(formData.max_value) : null;
      if (minV !== null && maxV !== null && minV > maxV) {
        toast.error("Min Value cannot be greater than Max Value.");
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        award_id: award.id,
        question_text: formData.question_text.trim(),
        question_type: formData.question_type,
        is_required: formData.is_required,
        options: formData.options || null,
        option_keys: formData.option_keys || null,
        min_value: formData.min_value !== "" ? parseInt(formData.min_value, 10) : null,
        max_value: formData.max_value !== "" ? parseInt(formData.max_value, 10) : null,
        min_length: formData.min_length !== "" ? parseInt(formData.min_length, 10) : null,
        max_length: formData.max_length !== "" ? parseInt(formData.max_length, 10) : null,
        allowed_extensions: formData.allowed_extensions || null,
        max_file_size_mb: formData.max_file_size_mb !== "" ? parseInt(formData.max_file_size_mb, 10) : null,
        sort_order: formData.sort_order !== "" ? parseInt(formData.sort_order, 10) : 0,
        is_active: formData.is_active,
        helper_text: formData.helper_text || null,
      };

      if (editQuestionData) {
        const originalData = {
          award_id: award.id,
          question_text: editQuestionData.question_text ? editQuestionData.question_text.trim() : "",
          question_type: editQuestionData.question_type || "text",
          is_required: editQuestionData.is_required ?? true,
          options: editQuestionData.options || null,
          option_keys: editQuestionData.option_keys || null,
          min_value: editQuestionData.min_value !== null && editQuestionData.min_value !== undefined && editQuestionData.min_value !== "" ? parseInt(editQuestionData.min_value, 10) : null,
          max_value: editQuestionData.max_value !== null && editQuestionData.max_value !== undefined && editQuestionData.max_value !== "" ? parseInt(editQuestionData.max_value, 10) : null,
          min_length: editQuestionData.min_length !== null && editQuestionData.min_length !== undefined && editQuestionData.min_length !== "" ? parseInt(editQuestionData.min_length, 10) : null,
          max_length: editQuestionData.max_length !== null && editQuestionData.max_length !== undefined && editQuestionData.max_length !== "" ? parseInt(editQuestionData.max_length, 10) : null,
          allowed_extensions: editQuestionData.allowed_extensions || null,
          max_file_size_mb: editQuestionData.max_file_size_mb !== null && editQuestionData.max_file_size_mb !== undefined && editQuestionData.max_file_size_mb !== "" ? parseInt(editQuestionData.max_file_size_mb, 10) : null,
          sort_order: editQuestionData.sort_order !== null && editQuestionData.sort_order !== undefined && editQuestionData.sort_order !== "" ? parseInt(editQuestionData.sort_order, 10) : 0,
          is_active: editQuestionData.is_active ?? true,
          helper_text: editQuestionData.helper_text || null,
        };

        const changedPayload = {};
        Object.keys(payload).forEach(key => {
          if (String(payload[key]) !== String(originalData[key])) {
             changedPayload[key] = payload[key];
          }
        });

        if (Object.keys(changedPayload).length === 0) {
          toast.info("No changes made.");
          setSubmitting(false);
          closeForm();
          return;
        }

        await apiCall(`/awards/questions/update/${editQuestionData.id}`, {
          method: "PUT",
          body: JSON.stringify(changedPayload),
        });
        toast.success("Question updated successfully!");
      } else {
        await apiCall("/awards/questions/create", {
          method: "POST",
          body: JSON.stringify(payload),
        });
        toast.success("Question added successfully!");
      }

      closeForm();
      fetchQuestions();
    } catch (error) {
      console.error("Failed to create question:", error);
      toast.error(error.message || "Failed to create question");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !award) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm overflow-hidden" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-100 bg-gray-50/50 shrink-0">
          <div>
            <h2 className="text-2xl font-black text-gray-800 tracking-tight">Award Questions</h2>
            <p className="text-sm font-semibold text-indigo-600 mt-1">{award.award_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          
          {/* Questions List */}
          <div className="flex-1 overflow-y-auto border-r border-gray-100 bg-gray-50/30 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-800">Existing Questions ({questions.length})</h3>
              {!isCreating && (
                <button
                  onClick={openCreateForm}
                  className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg shadow-sm transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 font-medium">Loading questions...</p>
              </div>
            ) : questions.length > 0 ? (
              <div className="space-y-4">
                {questions.map((q, idx) => (
                  <div key={q.id} className="bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200 group flex flex-col overflow-hidden relative">
                    <div className="flex items-start p-3 sm:p-4 gap-3">
                      {/* Left: Number */}
                      <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 font-bold text-sm flex items-center justify-center shrink-0 border border-indigo-100">
                        {q.sort_order ?? (idx + 1)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 flex flex-col gap-1 pr-16">
                        <div className="flex items-center flex-wrap gap-2">
                          <h4 className="text-base font-bold text-gray-800 break-words">
                            {q.question_text}
                          </h4>
                          {q.is_required && <span className="text-red-500 font-bold leading-none" title="Required">*</span>}
                        </div>
                        
                        {q.helper_text && (
                          <p className="text-xs text-gray-500 mt-0.5">{q.helper_text}</p>
                        )}

                        {/* Type Badge */}
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-[10px] font-bold text-gray-600 uppercase tracking-wider border border-gray-200">
                            {q.question_type}
                          </span>
                        </div>

                        {/* Options Row (only if present) */}
                        {['radio', 'checkbox', 'dropdown'].includes(q.question_type) && q.options && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {q.options.split(',').map((opt, i) => (
                              <div key={i} className="flex items-center px-2 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-xs font-medium rounded-md">
                                {q.question_type === 'radio' && <div className="w-2.5 h-2.5 rounded-full border-2 border-indigo-400 mr-1.5"></div>}
                                {q.question_type === 'checkbox' && <div className="w-2.5 h-2.5 rounded-[2px] border-2 border-indigo-400 mr-1.5"></div>}
                                {q.question_type === 'dropdown' && <List className="w-2.5 h-2.5 text-indigo-400 mr-1.5" />}
                                {opt.trim()}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Right: Edit Button */}
                      <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                        <button 
                          onClick={() => openEditForm(q)}
                          className="inline-flex items-center text-[10px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded transition-colors"
                          title="Edit Question"
                        >
                          <Edit2 className="w-3 h-3 mr-1" /> Edit
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center flex flex-col items-center justify-center h-[50vh]">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                  <List className="w-8 h-8 text-gray-300" />
                </div>
                <h4 className="text-gray-800 font-bold text-lg mb-2">No Questions Yet</h4>
                <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">
                  Add questions to this award to build out the nomination form for applicants.
                </p>
                {!isCreating && (
                  <button
                    onClick={openCreateForm}
                    className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg shadow-sm transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Question
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Create Question Form Pane */}
          {isCreating && (
            <div className="w-full md:w-[400px] lg:w-[450px] border-l border-gray-200 bg-white flex flex-col shrink-0 animate-in slide-in-from-right duration-300">
              <div className="p-5 border-b border-gray-100 bg-indigo-50/30 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">{editQuestionData ? "Edit Question" : "Add New Question"}</h3>
                <button
                  onClick={closeForm}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <form id="createQuestionForm" onSubmit={handleCreateSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Question Type *</label>
                    <div className="relative">
                      <select
                        name="question_type"
                        value={formData.question_type}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 appearance-none bg-white font-medium"
                      >
                        {questionTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Question Text *</label>
                    <textarea
                      name="question_text"
                      value={formData.question_text}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., What is your primary research focus?"
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 resize-none font-medium"
                    />
                  </div>

                  <div className="bg-white border border-gray-200 p-4 rounded-xl shadow-sm">
                    <label className="flex items-start gap-4 cursor-pointer">
                      <div className="relative shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          name="is_required"
                          checked={formData.is_required}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-bold text-gray-800">Required Field</span>
                        <p className="text-xs text-gray-500 mt-1 font-medium">If enabled, applicants will not be able to submit their nomination without answering this question.</p>
                      </div>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Helper Text (Optional)</label>
                    <input
                      type="text"
                      name="helper_text"
                      value={formData.helper_text}
                      onChange={handleInputChange}
                      placeholder="e.g., Please provide up to 500 words."
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 font-medium"
                    />
                  </div>

                  {['radio', 'checkbox', 'dropdown'].includes(formData.question_type) && (
                    <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-indigo-900 mb-1">Options (Comma separated) *</label>
                        <textarea
                          name="options"
                          value={formData.options}
                          onChange={handleInputChange}
                          required={['radio', 'checkbox', 'dropdown'].includes(formData.question_type)}
                          placeholder="e.g., Option A, Option B, Option C"
                          rows={2}
                          className="w-full px-3 py-2 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 resize-none font-medium bg-white"
                        />
                        <p className="text-xs text-indigo-600 mt-1 font-medium">Use commas to separate choices.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-indigo-900 mb-1">Option Keys (Optional, Comma separated)</label>
                        <textarea
                          name="option_keys"
                          value={formData.option_keys || ""}
                          onChange={handleInputChange}
                          placeholder="e.g., valA, valB, valC"
                          rows={2}
                          className="w-full px-3 py-2 border border-indigo-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm text-gray-700 resize-none font-medium bg-white"
                        />
                        <p className="text-xs text-indigo-600 mt-1 font-medium">Underlying values to save in database. Must match number of options.</p>
                      </div>
                    </div>
                  )}

                  {formData.question_type === 'file' && (
                    <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100 space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-amber-900 mb-2">Allowed Extensions</label>
                        <div className="flex flex-wrap gap-2">
                          {['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'webp', 'mp4', 'mov', 'avi', 'mkv'].map(ext => {
                             const currentExts = formData.allowed_extensions ? formData.allowed_extensions.split(',').map(e => e.trim().toLowerCase()).filter(Boolean) : [];
                             const isChecked = currentExts.includes(ext);
                             return (
                               <label key={ext} className="flex items-center gap-1.5 bg-white border border-amber-200 px-2 py-1.5 rounded-md cursor-pointer hover:bg-amber-50 transition-colors">
                                 <input 
                                   type="checkbox" 
                                   checked={isChecked}
                                   onChange={(e) => {
                                      let newExts = [...currentExts];
                                      if (e.target.checked && !newExts.includes(ext)) newExts.push(ext);
                                      else if (!e.target.checked) newExts = newExts.filter(e => e !== ext);
                                      setFormData({...formData, allowed_extensions: newExts.join(',')});
                                   }}
                                   className="text-amber-600 rounded border-gray-300 focus:ring-amber-500 cursor-pointer"
                                 />
                                 <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">{ext}</span>
                               </label>
                             )
                          })}
                        </div>
                        <p className="text-xs text-amber-700 mt-2 font-medium">Select the file types applicants are allowed to upload.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-amber-900 mb-1">Max File Size (MB)</label>
                        <input
                          type="number"
                          name="max_file_size_mb"
                          value={formData.max_file_size_mb}
                          onChange={handleInputChange}
                          onWheel={(e) => e.target.blur()}
                          min="1"
                          placeholder="e.g., 5"
                          className="w-full px-3 py-2 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none text-sm text-gray-700 font-medium bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {['text', 'textarea'].includes(formData.question_type) && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Min Length (words)</label>
                        <input type="number" name="min_length" value={formData.min_length} onChange={handleInputChange} onWheel={(e) => e.target.blur()} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Max Length (words)</label>
                        <input type="number" name="max_length" value={formData.max_length} onChange={handleInputChange} onWheel={(e) => e.target.blur()} min="1" className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm" />
                      </div>
                    </div>
                  )}

                  {formData.question_type === 'numeric' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Min Value</label>
                        <input type="number" name="min_value" value={formData.min_value} onChange={handleInputChange} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Max Value</label>
                        <input type="number" name="max_value" value={formData.max_value} onChange={handleInputChange} onWheel={(e) => e.target.blur()} className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none text-sm" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Sort Order</label>
                    <input
                      type="number"
                      name="sort_order"
                      value={formData.sort_order ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, sort_order: val === "" ? "" : Number(val) });
                      }}
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none text-sm font-medium"
                    />
                  </div>
                  
                  <div className="bg-white border border-gray-200 p-4 rounded-xl shadow-sm">
                    <label className="flex items-start gap-4 cursor-pointer">
                      <div className="relative shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active === true || formData.is_active === "1"}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-bold text-gray-800">Is Active</span>
                        <p className="text-xs text-gray-500 mt-1 font-medium">If enabled, this question will be active and shown to applicants. If disabled, it will be hidden.</p>
                      </div>
                    </label>
                  </div>

                </form>
              </div>
              
              <div className="p-5 border-t border-gray-100 bg-white shrink-0">
                <button
                  type="submit"
                  form="createQuestionForm"
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {submitting ? "Saving..." : editQuestionData ? "Update Question" : "Save Question"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>,
    document.body
  );
};

export default AwardQuestionsModal;
